import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Database,
  Plus,
  MagnifyingGlass,
  DotsThree,
  Eye,
  PencilSimple,
  Trash,
  ArrowLeft,
  FloppyDisk,
  Tag,
  X,
  MapPin,
  UploadSimple
} from '@phosphor-icons/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';
import ImportModal from '../../components/ImportModal';
import FileAttachments from '../../components/FileAttachments';

// Samples List Page
export const SamplesPage = () => {
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    loadSamples();
  }, []);

  const loadSamples = async () => {
    try {
      const data = await api.getSamples();
      setSamples(data);
    } catch (error) {
      console.error('Failed to load samples:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this sample?')) {
      try {
        await api.deleteSample(id);
        setSamples(samples.filter(s => s.id !== id));
        toast.success('Sample deleted');
      } catch (error) {
        toast.error('Failed to delete sample');
      }
    }
  };

  const filteredSamples = samples.filter(s =>
    s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.sample_type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.location?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="samples-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-display">Samples</h1>
          <p className="text-zinc-500 mt-1">Track lab samples and their locations</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" className="btn-secondary flex items-center gap-2" onClick={() => setShowImport(true)} data-testid="import-samples-btn">
            <UploadSimple size={16} /> Import
          </Button>
          <Link to="/samples/new">
            <Button className="btn-primary flex items-center gap-2" data-testid="new-sample-btn">
              <Plus size={16} /> New Sample
            </Button>
          </Link>
        </div>
      </div>

      <div className="card-flat p-4 mb-6">
        <div className="relative max-w-md">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            type="text"
            placeholder="Search samples..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 input-field w-full"
            data-testid="search-samples"
          />
        </div>
      </div>

      {filteredSamples.length > 0 ? (
        <div className="card-flat overflow-hidden">
          <table className="data-table" data-testid="samples-table">
            <thead>
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Type</th>
                <th className="table-header">Location</th>
                <th className="table-header">Position</th>
                <th className="table-header">Created</th>
                <th className="table-header w-12"></th>
              </tr>
            </thead>
            <tbody>
              {filteredSamples.map((sample) => (
                <tr key={sample.id} className="table-row" data-testid={`sample-row-${sample.id}`}>
                  <td className="table-cell">
                    <Link to={`/samples/${sample.id}`} className="font-medium text-slate-100 hover:text-indigo-300">
                      {sample.name}
                    </Link>
                  </td>
                  <td className="table-cell">
                    <span className="text-xs bg-blue-100 text-indigo-400 px-2 py-0.5 rounded-sm">
                      {sample.sample_type}
                    </span>
                  </td>
                  <td className="table-cell text-zinc-500">
                    {sample.location || '-'}
                  </td>
                  <td className="table-cell text-zinc-500 font-mono text-sm">
                    {sample.freezer_box && sample.position ? `${sample.freezer_box}:${sample.position}` : '-'}
                  </td>
                  <td className="table-cell text-zinc-500">
                    {new Date(sample.created_at).toLocaleDateString()}
                  </td>
                  <td className="table-cell">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button className="p-1 hover:bg-zinc-100 rounded-sm"><DotsThree size={20} /></button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/samples/${sample.id}`)}>
                          <Eye size={16} className="mr-2" /> View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/samples/${sample.id}/edit`)}>
                          <PencilSimple size={16} className="mr-2" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(sample.id)} className="text-red-400">
                          <Trash size={16} className="mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="empty-state card-flat">
          <Database size={48} className="text-zinc-300 mb-4" />
          <h3 className="heading-card text-slate-100 mb-2">No samples yet</h3>
          <p className="text-sm text-zinc-500 mb-4">Start tracking your lab samples</p>
          <Link to="/samples/new"><Button className="btn-primary">Add Sample</Button></Link>
        </div>
      )}
      {showImport && <ImportModal type="samples" onClose={() => setShowImport(false)} onImportComplete={loadSamples} />}
    </div>
  );
};

// Sample Form
export const SampleForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [plasmids, setPlasmids] = useState([]);
  const [strains, setStrains] = useState([]);
  const [tagInput, setTagInput] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    sample_type: 'Glycerol Stock',
    plasmid_id: '',
    strain_id: '',
    location: '',
    freezer_box: '',
    position: '',
    metadata: {},
    tags: []
  });

  const sampleTypes = ['Glycerol Stock', 'Plasmid DNA', 'Competent Cells', 'Protein', 'RNA', 'Lysate', 'Culture', 'Other'];

  useEffect(() => {
    loadOptions();
    if (isEdit && id) loadSample();
  }, [id, isEdit]);

  const loadOptions = async () => {
    try {
      const [plasmidsData, strainsData] = await Promise.all([
        api.getPlasmids(),
        api.getStrains()
      ]);
      setPlasmids(plasmidsData);
      setStrains(strainsData);
    } catch (error) {
      console.error('Failed to load options:', error);
    }
  };

  const loadSample = async () => {
    try {
      const data = await api.getSample(id);
      setFormData(data);
    } catch (error) {
      toast.error('Failed to load sample');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({ ...formData, tags: [...formData.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        await api.updateSample(id, formData);
        toast.success('Sample updated');
      } else {
        const result = await api.createSample(formData);
        toast.success('Sample created');
        navigate(`/samples/${result.id}`);
      }
    } catch (error) {
      toast.error('Failed to save sample');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  }

  return (
    <div data-testid="sample-form-page">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/samples" className="p-2 hover:bg-zinc-100 rounded-sm"><ArrowLeft size={20} /></Link>
        <div>
          <h1 className="heading-display">{isEdit ? 'Edit Sample' : 'New Sample'}</h1>
          <p className="text-zinc-500 mt-1">{isEdit ? 'Update sample details' : 'Add a new sample'}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <div className="card-flat p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} placeholder="Sample name" className="input-field w-full mt-1" required data-testid="sample-name-input" />
                </div>
                <div>
                  <Label htmlFor="sample_type">Type *</Label>
                  <select id="sample_type" name="sample_type" value={formData.sample_type} onChange={handleChange} className="input-field w-full mt-1">
                    {sampleTypes.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="plasmid_id">Associated Plasmid</Label>
                  <select id="plasmid_id" name="plasmid_id" value={formData.plasmid_id || ''} onChange={handleChange} className="input-field w-full mt-1">
                    <option value="">None</option>
                    {plasmids.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="strain_id">Associated Strain</Label>
                  <select id="strain_id" name="strain_id" value={formData.strain_id || ''} onChange={handleChange} className="input-field w-full mt-1">
                    <option value="">None</option>
                    {strains.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="card-flat p-6 space-y-4">
              <h3 className="heading-card flex items-center gap-2"><MapPin size={18} /> Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="location">Storage Location</Label>
                  <Input id="location" name="location" value={formData.location} onChange={handleChange} placeholder="e.g., -80°C Freezer A" className="input-field w-full mt-1" />
                </div>
                <div>
                  <Label htmlFor="freezer_box">Box</Label>
                  <Input id="freezer_box" name="freezer_box" value={formData.freezer_box} onChange={handleChange} placeholder="e.g., Box 1" className="input-field w-full mt-1" />
                </div>
                <div>
                  <Label htmlFor="position">Position</Label>
                  <Input id="position" name="position" value={formData.position} onChange={handleChange} placeholder="e.g., A1" className="input-field w-full mt-1" />
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="card-flat p-6">
              <Label>Tags</Label>
              <div className="flex gap-2 mt-2">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())} placeholder="Add tag" className="input-field flex-1" />
                <Button type="button" variant="outline" onClick={handleAddTag}><Tag size={16} /></Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                {formData.tags.map((tag, idx) => (
                  <span key={idx} className="inline-flex items-center gap-1 bg-zinc-100 text-zinc-700 px-2 py-1 text-sm rounded-sm">
                    {tag}
                    <button type="button" onClick={() => handleRemoveTag(tag)} className="hover:text-red-400"><X size={12} /></button>
                  </span>
                ))}
              </div>
            </div>

            <Button type="submit" className="btn-primary w-full" disabled={saving} data-testid="save-sample-btn">
              {saving ? <><div className="spinner w-4 h-4 mr-2" />Saving...</> : <><FloppyDisk size={16} className="mr-2" />{isEdit ? 'Update' : 'Create'} Sample</>}
            </Button>
          </div>
        </div>
      </form>

      {isEdit && id && (
        <div className="card-flat p-5 mt-4">
          <FileAttachments entityType="samples" entityId={id} />
        </div>
      )}
    </div>
  );
};

export default SamplesPage;
